import React, { ChangeEvent, RefObject } from "react";
import { useTranslation } from "react-i18next";

export interface ProgramImageUploadProps {
  selectedImage: File | null;
  images: string | null;
  imageError: string | null;
  fileInputRef: RefObject<HTMLInputElement>;
  onImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const ProgramImageUpload: React.FC<ProgramImageUploadProps> = ({
  selectedImage,
  images,
  imageError,
  fileInputRef,
  onImageChange,
}) => {
  const { t } = useTranslation();

  const previewSrc =
    selectedImage instanceof File
      ? URL.createObjectURL(selectedImage)
      : images || "";

  return (
    <div className="mt-4 mb-4">
      <input
        id="image"
        type="file"
        name="image"
        onChange={onImageChange}
        className="visually-hidden"
        accept="image/png, image/jpeg, image/jpg, image/svg+xml"
        ref={fileInputRef}
      />

      {selectedImage instanceof File || images ? (
        <div className="flex items-center gap-4">
          <div className="relative w-28 h-28 rounded-xl border-2 border-gray-200 bg-gray-50 flex items-center justify-center p-2 overflow-hidden shadow-xs">
            <img
              src={previewSrc}
              alt="preview"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-gilroy-bold text-[#037897] bg-[#E8F5F8] hover:bg-[#d0eff5] active:scale-98 rounded-lg transition-all border border-[#037897]/25 shadow-xs"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
              <span>
                {t("adminPrograms.basicForm.replaceImage", "Replace Image")}
              </span>
            </button>
            <span className="text-[11px] text-gray-400 font-gilroy">
              PNG, JPG, SVG (Max 1MB)
            </span>
          </div>
        </div>
      ) : (
        <label
          htmlFor="image"
          className={`flex flex-col items-center justify-center w-36 h-36 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200 ${
            imageError
              ? "border-red-400 bg-red-50/70 hover:bg-red-100/60 ring-4 ring-red-100"
              : "border-gray-300 bg-gray-50 hover:bg-gray-100"
          }`}
        >
          <div className="flex flex-col items-center justify-center p-3 text-center">
            <img
              src="/images/gallery-add.png"
              alt="upload"
              className="w-7 h-7 mb-2 object-contain"
            />
            <p
              className={`text-xs font-gilroy-bold ${
                imageError ? "text-red-600" : "text-[#4C535F]"
              }`}
            >
              {t("adminPrograms.basicForm.uploadIcon", "Upload Icon")}
            </p>
            <p
              className={`text-[10px] mt-1 ${
                imageError
                  ? "text-red-400 font-gilroy-medium"
                  : "text-gray-400"
              }`}
            >
              PNG, JPG, SVG (Max 1MB)
            </p>
          </div>
        </label>
      )}

      {imageError && (
        <div className="flex items-center gap-2 mt-2.5 px-3 py-1.5 bg-red-50 border border-red-200 text-red-600 rounded-lg text-xs font-gilroy-medium w-fit">
          <svg
            className="w-4 h-4 shrink-0 text-red-500"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span>{imageError}</span>
        </div>
      )}
    </div>
  );
};

export default ProgramImageUpload;
